import * as React from "react";
import { Button, Checkbox, Form, Input, Row, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { LOGIN_SUCCESS } from "../../redux/auth/authType";
import { responseNotification } from "../../utils/notify";
import { debounce } from "lodash";

export const BusinessCreateForm: React.FC = () => {
  const { type, token } = useSelector((state: any) => state.authReducer);

  const [operationType, setOperationType] = useState("INDIVIDUAL");
  const [businessTypeId, setBusinessTypeId] = useState<any>(null);
  const [serviceId, setServiceId] = useState<any>(null);

  const [companyId, setCompanyId] = useState<any>();
  const [merchantDetails, setMerchantDetails] = useState<any>(null);

  const [businessCategoryId, setBusinessCategoryId] = useState("");
  const [businessCuisineId, setBusinessCuisineId] = useState("");

  const [isChecked, setIsChecked] = useState(false);

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  // ================== OPTIONS =====================
  const [businessTypeOptions, setBusinessTypeOptions] = useState<any>({ list: [] });
  const [serviceOptions, setServiceOptions] = useState<any>({ list: [] });
  const [companyOptions, setCompanyOptions] = useState<any>({ list: [] });
  const [businessCategoryOptions, setBusinessCategoryOptions] = useState<any>({ list: [] });
  const [businessCuisineOptions, setBusinessCuisineOptions] = useState<any>({ list: [] });

  // ================== LOAD BUSINESS TYPE =====================
  const loadBusinessTypes = useCallback(async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_CATALOG_WRITER_API}/shop/service-type-list?page=0&limit=20`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setBusinessTypeOptions({
      list: res.data.businessTypes.map((b: any) => ({ value: b.id, label: b.name })),
    });
  }, []);

  // ================== LOAD SERVICE TYPE =====================
  const loadServiceTypes = useCallback(async () => {
    if (!businessTypeId) return;

    const res = await axios.get(
      `${process.env.REACT_APP_CATALOG_WRITER_API}/shop/details-business-type?id=${businessTypeId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setServiceOptions({
      list: res.data.businessType.serviceTypes.map((s: any) => ({
        value: s.value,
        label: s.name,
      })),
    });
  }, [businessTypeId]);

  // ================== FETCH MERCHANT DETAILS =====================
  const fetchMerchantDetails = useCallback(async () => {
    const res = await axios.get(`${process.env.REACT_APP_RIDER_API}/details`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status === 200) {
      const md = res.data.merchant;
      setMerchantDetails(md);

      setOperationType(md.businessOperationType);
      setBusinessTypeId(md.businessTypeId);
      setServiceId(md.serviceType);

      form.setFieldsValue({
        businessOperationType: md.businessOperationType,
        businessTypeId: md.businessTypeId,
        serviceTypeId: md.serviceType,
        name: md.shopName,
        companyName: md.companyName,
      });
    }
  }, []);

  // ================== USE EFFECTS =====================
  useEffect(() => {
    loadBusinessTypes();
    fetchMerchantDetails();
  }, []);

  useEffect(() => {
    loadServiceTypes();
  }, [businessTypeId]);

  // ================== SUBMIT =====================
  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      // First API
      await fetch(`${process.env.REACT_APP_RIDER_API}/merchant/choose-business-model`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessOperationType: operationType,
          businessTypeId: businessTypeId,
          companyName: data.companyName,
        }),
      });

      // Second API
      const createRes = await fetch(
        `${process.env.REACT_APP_CATALOG_WRITER_API}/shop/create-shop-by-sm`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: data.serviceTypeId,
            businessCategoryId,
            businessSubCategoryId: businessCuisineId,
            name: data.name,
            isActive: false,
          }),
        }
      );

      const createJson = await createRes.json();

      if (createJson.statusCode === 200) {
        dispatch({
          type: LOGIN_SUCCESS,
          payload: { ...createJson, token, shopInfo: createJson.shop, shopId: createJson.shop.id },
        });

        responseNotification("Business Created Successfully", "success");
        navigate("/merchant-profile");
      }
    } catch (err) {
      responseNotification("Internal Server Error", "error");
    } finally {
      setLoading(false);
    }
  };

  // ================== UI =====================
  return (
    <div className="flex overflow-hidden flex-col bg-stone-50 pb-[10px]">
      <main className="flex flex-col bg-white p-4 self-center mt-5 w-[50%] rounded-2xl">
        <h1 className="text-xl font-bold pb-4">Create Business</h1>

        <Form form={form} layout="vertical" onFinish={onSubmit}>
          {/* OPERATION TYPE */}
          <Form.Item
            label="Business Operation Type"
            name="businessOperationType"
            rules={[{ required: true, message: "Business Operation Type is required!" }]}
          >
            <Select
              value={operationType}
              onChange={(val) => {
                setOperationType(val);
                form.setFieldsValue({ businessOperationType: val });
              }}
              options={[
                { value: "INDIVIDUAL", label: "None, I am an Individual" },
                { value: "COMPANY", label: "As a Company" },
              ]}
            />
          </Form.Item>

          {/* BUSINESS TYPE */}
          <Form.Item
            label="Business Type"
            name="businessTypeId"
            rules={[{ required: true, message: "Business Type is required!" }]}
          >
            <Select
              value={businessTypeId}
              onChange={(val) => {
                setBusinessTypeId(val);
                form.setFieldsValue({ businessTypeId: val });
              }}
              options={businessTypeOptions.list}
            />
          </Form.Item>

          {/* SERVICE TYPE */}
          <Form.Item
            label="Service Type"
            name="serviceTypeId"
            rules={[{ required: true, message: "Service Type is required!" }]}
          >
            <Select
              value={serviceId}
              onChange={(val) => {
                setServiceId(val);
                form.setFieldsValue({ serviceTypeId: val });
              }}
              options={serviceOptions.list}
            />
          </Form.Item>

          {/* BUSINESS NAME */}
          <Form.Item label="Business Name" name="name" rules={[{ required: true }]}>
            <Input placeholder="Enter Business name" />
          </Form.Item>

          {/* SUBMIT */}
          <Button loading={loading} type="primary" htmlType="submit">
            Submit
          </Button>
        </Form>
      </main>
    </div>
  );
};

export default BusinessCreateForm;
